import "styles/onboarding/onboarding.less";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Col, Form, Row, Typography } from "antd";
import backIcon from "assets/images/onboarding/back-chevron.svg";
import ProfileButtonFooter from "components/basic/Footer/ProfileButtonFooter";
import FormItemWrapper from "components/basic/FormWrapper/FormItemWrapper";
import AutoCompleteAddressInput from "components/basic/SignUpForm/AutoCompleteAddressInput";
import { checkDepend, EnableValidationT, ErrorsT, FormDataT, WarningsT } from "components/basic/SignUpForm/SignUpForm";
import TaxYearSelection from "components/common/TaxYearSelection";
import FloatDatePicker from "components/inputs/Form/Inputs/FloatDatePicker";
import FloatInput from "components/inputs/Form/Inputs/FloatInput";
import UkPhoneInputWithCountryCode from "components/inputs/Form/Inputs/PhoneInputWithCountryCode";
import SelectOneTile from "components/inputs/Form/Inputs/SelectOneTile";
import dayjs from "dayjs";
import { useBankConnection } from "hooks/bank/useBankConnection";
import { useUser } from "hooks/common/useUser";
import { useLocalStorage } from "hooks/helpers/useLocalStorage";
import Lottie from "lottie-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { selectDocument, setCurrentDocumentYear, setSelectedSharingPreference, setTaxService } from "redux/reducers/document";
import { next } from "redux/reducers/navigation";
import { useUpdateCurrentUserMutation } from "services/auth";
import { checkNinAvailability } from "services/client";
import { IUpdateUserRequestBody } from "types/auth";
import { SharingPreferenceIdType } from "types/common";
import { logError } from "utils/sentry";
import { isNINValid } from "utils/validation";

import { OnboardingInputIds, OnboardingInputType, OnboardingStep, onboardingSteps } from "./constants/onboarding";
import { logGoogleAnalyticsEvent } from "../../../utils/analytics";
import ConnectPlaidModal from "../Questionnaire/components/OpenBanking/ConnectPlaidModal";

export const getNextValidStepIndex = (
    currentStepIndex: number,
    direction: "forward" | "backward",
    steps: OnboardingStep[],
    inputData: Record<string, any>
): number => {
    let index = currentStepIndex;

    while (true) {
        index = direction === "forward" ? index + 1 : index - 1;

        if (index < 0 || index >= steps.length) {
            break;
        }

        const step = steps[index];
        const shouldShowStep = checkDepend(step.depend, inputData);

        if (shouldShowStep) {
            return index;
        }
    }

    return currentStepIndex; // fallback if no valid step is found
};

const Onboarding: React.FC = () => {
    const { user } = useUser();
    const { currentDocumentYear, taxService, selectedSharingPreference } = useAppSelector(selectDocument);
    const connectedBankAccounts = useAppSelector(state => state.bankAccounts.connectedBankAccounts);
    const [, setInitialBankCount] = useLocalStorage<number>("number_bank_accounts", 0);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState<ErrorsT>({});
    const [warnings] = useState<WarningsT>({});
    const [enableValidation, setEnableValidation] = useState<EnableValidationT>({});
    const [inputData, setInputData] = useState<FormDataT>({});
    const [updateCurrentUser] = useUpdateCurrentUserMutation();

    const currentStepData = useMemo(() => onboardingSteps[currentStep], [currentStep]);

    const initialInputData = useMemo(() => {
        if (!user) return {};

        return {
            [OnboardingInputIds.PHONE]: user.info?.phone || "",
            [OnboardingInputIds.DOB]: user.info?.dateOfBirth || "",
            [OnboardingInputIds.NIN]: user.nin || "",
            [OnboardingInputIds.UTR]: user.utr || "",
            [OnboardingInputIds.AUTOCOMPLETE_ADDRESS]: user.address?.autocompleteAddress || "",
            [OnboardingInputIds.ADDRESS1]: user.address?.address1 || "",
            [OnboardingInputIds.ADDRESS2]: user.address?.address2 || "",
            [OnboardingInputIds.CITY]: user.address?.city || "",
            [OnboardingInputIds.POSTCODE]: user.address?.postcode || "",
            [OnboardingInputIds.COUNTRY]: user.address?.country || "",
            [OnboardingInputIds.TAX_YEAR]: currentDocumentYear || "",
            [OnboardingInputIds.TAX_SERVICE]: taxService || "",
            [OnboardingInputIds.SHARING_PREFERENCE]: selectedSharingPreference || "",
            // Add others based on your onboardingSteps
        };
    }, [user, currentDocumentYear, taxService, selectedSharingPreference]);

    const ninToCheck = inputData["nin"];
    const isFormatValid = isNINValid(ninToCheck);

    const isCurrentStepValid = useMemo(() => {
        if (!currentStepData?.inputs) return true;

        return currentStepData.inputs.every(input => {
            const shouldShow = checkDepend(input.depend, inputData);
            if (!shouldShow) return true; // Don't validate hidden fields

            const inputEnabled = enableValidation[input.id];
            const hasError = errors[input.id];

            if (inputEnabled && hasError) return false;

            if (input.required && !inputData[input.id]) return false;

            return true;
        });
    }, [currentStepData, inputData, enableValidation, errors]);

    const {
        data: ninAvailability,
        isLoading: isCheckingNin,
        error: ninError,
    } = checkNinAvailability.useQuery(
        { nin: ninToCheck! },
        { skip: !isFormatValid }
    );

    const {
        linkToken,
        isModalOpen,
        setIsModalOpen,
        openConnectModal,
    } = useBankConnection({
        user: user!,
        connectedBankAccountsLength: connectedBankAccounts.length,
        setInitialBankCount,
    });

    const nextStep = useCallback(() => {
        if (!isCurrentStepValid || !user) return;

        logGoogleAnalyticsEvent(`onboarding_next_clicked ${currentStepData.title}`, {
            step_title: currentStepData.title,
            step_key: currentStepData.id ?? currentStep.toString(),
            tax_year: inputData[OnboardingInputIds.TAX_YEAR] || null,
            platform: window.innerWidth <= 768 ? "mobile" : "desktop",
            user_id: user.id,
        });

        const goToNextStep = () => {
            const nextIndex = getNextValidStepIndex(currentStep, "forward", onboardingSteps, inputData);
            const nextStep = onboardingSteps[nextIndex];
            if (nextIndex !== currentStep) {
                setCurrentStep(nextIndex);
            } else if (nextStep?.url) {
                if (inputData[OnboardingInputIds.SHARING_PREFERENCE] === SharingPreferenceIdType.connect) {
                    openConnectModal();
                } else {
                    navigate(nextStep.url);
                }
            }
        };

        const requestBody: IUpdateUserRequestBody = {
            info: {
                ...user.info,
                phone: inputData[OnboardingInputIds.PHONE] || "",
                dateOfBirth: inputData[OnboardingInputIds.DOB] || "",
            },
            address: {
                autocompleteAddress: inputData[OnboardingInputIds.AUTOCOMPLETE_ADDRESS] || "",
                address1: inputData[OnboardingInputIds.ADDRESS1] || "",
                address2: inputData[OnboardingInputIds.ADDRESS2] || "",
                city: inputData[OnboardingInputIds.CITY] || "",
                postcode: inputData[OnboardingInputIds.POSTCODE] || "",
                country: inputData[OnboardingInputIds.COUNTRY] || "",
            },
            nin: inputData[OnboardingInputIds.NIN] || "",
            utr: inputData[OnboardingInputIds.UTR] || "",
        };

        switch (currentStepData.nextAction) {
        case "DISPATCH_ACTION":
            dispatch(setCurrentDocumentYear(inputData[OnboardingInputIds.TAX_YEAR] || ""));
            dispatch(setTaxService(inputData[OnboardingInputIds.TAX_SERVICE] || ""));
            dispatch(setSelectedSharingPreference(inputData[OnboardingInputIds.SHARING_PREFERENCE] || SharingPreferenceIdType.connect));
            dispatch(next({
                nextCategory: inputData[OnboardingInputIds.SHARING_PREFERENCE] === SharingPreferenceIdType.connect ? 0 : 1,
                nextStep: 0,
                nextQuestion: 0,
                nextProfileQuestion: 0,
            }));
            goToNextStep();
            return;

        case "UPDATE_CLIENT":

            updateCurrentUser(requestBody)
                .unwrap()
                .then(goToNextStep)
                .catch(error => {
                    logError(`Error updating client ${user.id}`, {
                        userId: user.id,
                        step: currentStepData,
                        inputData,
                        error,
                    });
                });
            return;

        default:
            break;
        }

        if (currentStepData.hasNext) {
            goToNextStep();
        }
    }, [
        currentStepData,
        currentStep,
        isCurrentStepValid,
        inputData,
        user,
        navigate,
        updateCurrentUser,
        dispatch,
        openConnectModal,
    ]);

    const prevStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    }, [currentStep, setCurrentStep]);

    const calculateMinAgeDate = useCallback(() => {
        const maxDate = dayjs().subtract(18, "year").endOf("year");
        return maxDate.format("YYYY/MM/DD");
    }, []);

    const handleInputChange = ({
        name,
        value,
        object,
    }: {
        name?: string;
        value?: string | string[];
        object?: { [key: string]: string };
    }) => {
        if (name) {
            const inputDef = currentStepData.inputs?.find(i => i.id === name);

            // Validate only this field
            if (inputDef?.validate) {
                const error = inputDef.validate(value as string, {
                    ...inputData,
                    ...object,
                });
                setErrors(prev => ({
                    ...prev,
                    [name]: error || "",
                }));
            }

            setEnableValidation(prev => ({
                ...prev,
                [name]: true,
            }));

            setInputData(prev => ({
                ...prev,
                ...object,
                [name]: value,
            }));
        } else if (object) {
            setInputData(prev => ({
                ...prev,
                ...object,
            }));
        } else {
            throw new Error("Name or object are required");
        }
    };

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === "Enter") {
            event.preventDefault();
            nextStep();
        }
    }, [nextStep]);

    const closeConnectModal = useCallback(() => {
        setIsModalOpen(false);
        navigate("/profile");
    }, [setIsModalOpen, navigate]);

    useEffect(() => {
        if (!enableValidation["nin"]) return;
        if (isCheckingNin) return;
        if (!isFormatValid) return;

        const enteredNin = ninToCheck?.toUpperCase().trim();
        const currentUserNin = user?.nin?.toUpperCase().trim();

        if (ninAvailability?.available === false) {
            if (enteredNin !== currentUserNin) {
                setErrors(prev => ({ ...prev, nin: "This NIN is already registered" }));
            } else {
                setErrors(prev => {
                    // eslint-disable-next-line no-unused-vars
                    const { nin, ...rest } = prev;
                    return rest;
                });
            }
        } else if (ninError) {
            setErrors(prev => ({ ...prev, nin: "Error checking NIN availability" }));
        } else if (ninAvailability?.available === true) {
            setErrors(prev => {
                // eslint-disable-next-line no-unused-vars
                const { nin, ...rest } = prev;
                return rest;
            });
        }
    }, [ninAvailability, isCheckingNin, enableValidation, ninError, ninToCheck, user, isFormatValid]);

    useEffect(() => {
        window.history.pushState(null, "", window.location.href);

        const handlePopState = (event: PopStateEvent) => {
            event.preventDefault();
            window.history.pushState(null, "", window.location.href);
            if (currentStepData.hasPrev) {
                prevStep();
            }
        };

        window.addEventListener("popstate", handlePopState);
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [prevStep, currentStepData.hasPrev]);

    useEffect(() => {
        if (user && Object.keys(inputData).length === 0) {
            setInputData(initialInputData);

            const enabledFields = Object.keys(initialInputData).reduce((acc, key) => {
                acc[key] = true;
                return acc;
            }, {} as EnableValidationT);

            setEnableValidation(enabledFields);
        }
    }, [user, initialInputData, inputData]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    return (
        <div className="onboarding" data-testid="onboarding-form">
            <Row className="onboarding__header">
                <Col span={4}>{currentStepData.hasPrev && <img src={backIcon} alt="Back" role="button"className="pointer" onClick={prevStep}/>}</Col>
                <Col span={16}><Typography className="taCenter">{currentStepData.headerTitle}</Typography></Col>
                <Col span={4}></Col>
            </Row>
            <Typography className="onboarding__title">{currentStepData.title}</Typography>
            <div className="onboarding__description mb10">{currentStepData.description}</div>
            <div className="onboarding__animation">
                {currentStepData.animation && <Lottie
                    animationData={currentStepData.animation}
                    loop={false}
                    autoplay={true}
                />}
            </div>
            {currentStepData.image && <img src={currentStepData.image} alt={currentStepData.title} className="onboarding__image" />}

            {inputData && currentStepData.inputs &&
            <Form className="onboarding__form" data-testid="onboarding-form" layout="vertical">
                {currentStepData?.inputs.map((input, index) => {
                    const showQuestion = checkDepend(
                        input.depend,
                        inputData
                    );
                    if (!showQuestion) return null;

                    switch (input.type) {
                    case OnboardingInputType.DATE:
                        return (
                            <FormItemWrapper
                                key={input.id}
                                helpText={input.helpText}
                                error={errors[input.id]}
                                warning={warnings[input.id]}
                                label={input.label}
                                required={input.required}
                            >
                                <FloatDatePicker
                                    value={inputData[input.id]}
                                    placeholder={input.placeholder}
                                    error={errors[input.id]}
                                    maxDate={calculateMinAgeDate()}
                                    onChange={date => {
                                        handleInputChange({
                                            name: input.id,
                                            value: date,
                                        });
                                    }}
                                    suffix={input.suffix}
                                />
                            </FormItemWrapper>
                        );
                    case OnboardingInputType.PHONE:
                        return (
                            <FormItemWrapper
                                key={input.id}
                                error={errors[input.id]}
                                warning={warnings[input.id]}
                                helpText={input.helpText}
                                required={input.required}
                                label={input.label}
                            >
                                <UkPhoneInputWithCountryCode
                                    index={index}
                                    inputName={input.label}
                                    inputId={input.id}
                                    value={inputData[input.id]}
                                    error={errors[input.id]}
                                    suffix={input.suffix}
                                    onChange={(
                                        fullPhoneNumber: string
                                    ) => {
                                        handleInputChange({
                                            name: input.id,
                                            value: fullPhoneNumber,
                                        });
                                    }}
                                    handleKeyDown={handleKeyDown}
                                />
                            </FormItemWrapper>
                        );

                    case OnboardingInputType.AUTOCOMPLETE_ADDRESS: {
                        return (
                            <div key={input.id}>
                                <FormItemWrapper
                                    error={errors[input.id]}
                                    warning={warnings[input.id]}
                                    helpText={input.helpText}
                                    required={input.required}
                                    label={input.label}
                                >
                                    <AutoCompleteAddressInput
                                        placeholder={input.placeholder}
                                        value={inputData[input.id]}
                                        onChange={(value, object) => {
                                            handleInputChange({
                                                name: input.id,
                                                value,
                                                object,
                                            });
                                        }}
                                        suffix={input.suffix}
                                        handleKeyDown={handleKeyDown}
                                    />
                                </FormItemWrapper>
                            </div>
                        );
                    }

                    case OnboardingInputType.SELECT_TAX_YEAR:
                        return (
                            <FormItemWrapper
                                key={input.id}
                                error={errors[input.id]}
                                warning={warnings[input.id]}
                                helpText={input.helpText}
                                required={input.required}
                                label={input.label}
                            >
                                <TaxYearSelection
                                    onSelect={(value: string) => {
                                        handleInputChange({
                                            name: input.id,
                                            value,
                                        });
                                    }}
                                    placeholder={input.placeholder}
                                    value={inputData[input.id]}
                                />
                            </FormItemWrapper>
                        );
                    case OnboardingInputType.SELECT_ONE_TILE:
                        return (
                            <FormItemWrapper
                                key={input.id}
                                error={errors[input.id]}
                                warning={warnings[input.id]}
                                helpText={input.helpText}
                                required={input.required}
                                label={input.label}
                            >
                                <SelectOneTile
                                    index={index}
                                    inputId={input.id}
                                    value={inputData[input.id]}
                                    options={input.options!}
                                    error={errors[input.id]}
                                    suffix={input.suffix}
                                    onChange={(value: string) => {
                                        handleInputChange({
                                            name: input.id,
                                            value,
                                        });
                                    }}
                                    handleKeyDown={handleKeyDown}
                                />
                            </FormItemWrapper>
                        );
                    case OnboardingInputType.TEXT:
                    case OnboardingInputType.EMAIL:
                    default:
                        return (
                            <FormItemWrapper
                                key={input.id}
                                error={errors[input.id]}
                                warning={warnings[input.id]}
                                helpText={input.helpText}
                                label={input.label}
                                required={input.required}
                            >
                                <FloatInput
                                    required={input.required}
                                    index={index}
                                    value={inputData[input.id]}
                                    placeholder={input.placeholder}
                                    error={errors[input.id]}
                                    onChange={(event: any) => {
                                        handleInputChange({
                                            name: input.id,
                                            value: event.target.value,
                                        });
                                    }}
                                    type={input.type}
                                    suffix={input.suffix}
                                />
                            </FormItemWrapper>
                        );
                    }
                })}
            </Form>}

            <ProfileButtonFooter>
                <div className="onboarding__controls w100">
                    {currentStepData.hasNext && <button
                        onClick={nextStep}
                        className={`shared__primary-button w100 ${!isCurrentStepValid ? "disabled" : ""}`}

                        disabled={!isCurrentStepValid}
                    >
                        Next
                    </button>
                    }
                </div>
            </ProfileButtonFooter>

            {linkToken && isModalOpen && (
                <ConnectPlaidModal
                    linkToken={linkToken}
                    onSuccess={() => {
                        closeConnectModal();
                    }}
                    onExit={() => {
                        setIsModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default Onboarding;
